import { useState, type ReactNode, Fragment } from "react";
import {
  Alert,
  Box,
  Column,
  SectionHeading,
  TextWithTooltip,
} from "@hightouch/htui";
import * as Sentry from "src/lib/monitoring";
import {
  type BarChartData,
  BarChartDataSchema,
} from "@hightouch/lib/llm/schema/components/schemas";
import { AnalyticsGraph } from "src/components/charts/engine/analytics-graph";
import { GraphScale } from "src/components/charts/engine/constants";
import { valueFormatter } from "src/components/charts/engine/utils";
import { GraphType } from "@hightouch/lib/query/visual/types";
import { AnalyticsBar } from "src/components/analytics/breakdowns/analytics-bar";
import {
  formatBreakdownValue,
  getMaxBreakdownValue,
  getMaxDecimalPlaces,
} from "src/components/analytics/breakdowns/utils";
import {
  transformChartToGraphSeries,
  transformChartToHorizontalBarGraphSeries,
} from "./chart-transformer";
import { ChartWithAxisLabels } from "./chart-axis-labels";
import { Card } from "src/components/card";
import {
  DEFAULT_CHART_HEIGHT,
  parseJsonChildren,
  DEFAULT_VERTICAL_CHART_HEIGHT,
  COMPACT_CHART_HEIGHT,
} from "./chart-utils";

// Percentage-mode bars render against a full-length track marking 100%,
// in a light gray that reads as a backdrop rather than a stacked segment.
const PERCENTAGE_TRACK_COLOR = "gray.100";

// An out-of-range fraction from the agent must never kill or distort a
// percentage chart — clamp to [0, 1] once where the chart JSON is parsed.
// Only numeric values are clamped; the agent-written tooltip text stays
// untouched, so it may still state the raw out-of-range claim.
const clampFraction = (value: number) => Math.min(Math.max(value, 0), 1);

const clampPercentageValues = (chartData: BarChartData): BarChartData => ({
  ...chartData,
  series: chartData.series.map((series) => ({
    ...series,
    data: series.data.map((point) => ({
      ...point,
      value: clampFraction(point.value),
    })),
  })),
});

/**
 * Vertical bar chart (column chart) component
 */
const VerticalBarChart = ({
  chartData,
  compact = false,
}: {
  chartData: BarChartData;
  compact?: boolean;
}) => {
  const [hoveredLine, setHoveredLine] = useState<string | undefined>();

  // Extract categorical labels from the first series
  const categoricalLabels = chartData.series[0]?.data.map((d) => d.label) ?? [];

  // Transform to GraphSeries format
  const graphSeries = transformChartToGraphSeries(chartData);

  const graph = (
    <ChartWithAxisLabels
      xAxisLabel={chartData.config.xAxisLabel}
      yAxisLabel={chartData.config.yAxisLabel}
    >
      <AnalyticsGraph
        type={GraphType.Column}
        graphSeries={graphSeries}
        categoricalLabels={categoricalLabels}
        hoveredLine={hoveredLine}
        onHoverLine={setHoveredLine}
        stackBars={false}
        // Percentage mode degrades gracefully on the vertical path: the
        // shared graph formats ticks and tooltips as percentages, but its
        // axis still scales to the data rather than locking to 100%.
        scale={
          chartData.config.percentage ? GraphScale.Percent : GraphScale.Linear
        }
        height={compact ? COMPACT_CHART_HEIGHT : DEFAULT_CHART_HEIGHT}
      />
    </ChartWithAxisLabels>
  );

  if (compact) {
    return <Box py={2}>{graph}</Box>;
  }

  return (
    <Card my={4} p={4}>
      <Column gap={2}>
        <SectionHeading>{chartData.config.title}</SectionHeading>
        {graph}
      </Column>
    </Card>
  );
};

// Scale max bar width to ~85% of the value column so the numeric label after
// the bar (e.g. "0.286") never gets pushed past the column edge and clipped.
// Without this, top performers whose bar fills the column lose their label.
const HORIZONTAL_BAR_SCALE_PERCENTAGE = 85;

/**
 * Horizontal bar chart component. Renders its own label + bar rows instead
 * of the shared analytics BreakdownTable, whose wide monospace label column
 * reads as dead space and terminal styling when embedded as inline evidence
 * in chats and insight cards.
 */
const HorizontalBarChart = ({
  chartData,
  compact = false,
}: {
  chartData: BarChartData;
  compact?: boolean;
}) => {
  const graphSeries = transformChartToHorizontalBarGraphSeries(chartData);
  const hasMultipleSeries = chartData.series.length > 1;
  const isPercentage = chartData.config.percentage === true;

  const maxValue = getMaxBreakdownValue(graphSeries, false);
  const maxDecimalPlaces = getMaxDecimalPlaces(graphSeries);

  const rows = (
    <Box
      display="grid"
      // Label column hugs its content (capped so a long label truncates
      // instead of pushing every bar to the far edge); bars take the rest.
      gridTemplateColumns="fit-content(40%) minmax(0, 1fr)"
      columnGap={4}
      alignItems="center"
      sx={{ gridAutoRows: "minmax(32px, auto)" }}
    >
      {graphSeries.map((row, index) => {
        const metricValue = row.data[0]?.metricValue ?? 0;
        const seriesName = row.grouping?.[0]?.value;
        const value = isPercentage
          ? valueFormatter(metricValue, GraphScale.Percent)
          : formatBreakdownValue(metricValue, maxDecimalPlaces);
        // With multiple series, two rows can share a label — the series name
        // is the only thing distinguishing them, so always include it in the
        // tooltip alongside the point's own description.
        const tooltipText = [
          row.data[0]?.description,
          hasMultipleSeries && seriesName ? `Series: ${seriesName}` : undefined,
        ]
          .filter(Boolean)
          .join(" • ");
        return (
          // row.key is series+label, which collides if the agent repeats a
          // label within a series — suffix the index to keep keys unique.
          <Fragment key={`${row.key}-${index}`}>
            <TextWithTooltip size="sm" message={row.metricName}>
              {row.metricName}
            </TextWithTooltip>
            <AnalyticsBar
              metricName={row.metricName}
              value={value}
              valueVariant="default"
              color={row.color}
              // Percentage mode locks the scale to 100% (values are clamped
              // fractions) instead of scaling to the data max.
              width={
                isPercentage
                  ? metricValue * 100
                  : maxValue > 0
                    ? (metricValue / maxValue) * 100
                    : 0
              }
              barScalePercentage={HORIZONTAL_BAR_SCALE_PERCENTAGE}
              trackColor={isPercentage ? PERCENTAGE_TRACK_COLOR : undefined}
              tooltip={{
                type: "default",
                defaultTooltipProps: {
                  tooltipValue: value,
                  tooltipText: tooltipText || undefined,
                },
              }}
            />
          </Fragment>
        );
      })}
    </Box>
  );

  const chartRows = (
    <Column
      width="100%"
      maxHeight={compact ? undefined : DEFAULT_VERTICAL_CHART_HEIGHT}
      overflow={compact ? undefined : "auto"}
    >
      {rows}
    </Column>
  );

  if (compact) {
    // Compact charts grow to their natural height — they have a small,
    // bounded row count, and an inner scrollbar reads as broken when
    // embedded as inline evidence.
    return <Box py={2}>{chartRows}</Box>;
  }

  return (
    <Card my={4} p={4} pb={2}>
      <Column gap={4}>
        <SectionHeading>{chartData.config.title}</SectionHeading>
        {chartRows}
      </Column>
    </Card>
  );
};

/**
 * Bar chart component for displaying categorical performance data from LLM agents.
 *
 * Data is passed as children content (JSON string). The legacy `data` attribute is also
 * supported for backward compatibility with old campaign agent responses that use the
 * "groupName" field instead of "label".
 */
export const BarChart = ({
  data,
  children,
  compact = false,
}: {
  data?: string;
  children?: ReactNode;
  compact?: boolean;
}) => {
  try {
    let chartData: BarChartData;

    // If data attribute is provided, it may be legacy format with "groupName" or newer format with "label"
    if (data) {
      const parsedData = JSON.parse(data);
      // Handle both legacy format (groupName) and newer format (label)
      const convertedData = {
        config: parsedData.config,
        series: parsedData.series?.map((series: any) => ({
          name: series.name,
          data: series.data?.map((point: any) => ({
            label: point.label ?? point.groupName,
            value: point.value,
            tooltip: point.tooltip,
          })),
        })),
      };
      chartData = BarChartDataSchema.parse(convertedData);
    } else {
      // New format: parse directly using utility
      chartData = parseJsonChildren(children, BarChartDataSchema);
    }

    if (chartData.config.percentage) {
      chartData = clampPercentageValues(chartData);
    }

    // Delegate to appropriate component based on orientation
    const orientation = chartData.config.orientation ?? "vertical";

    return orientation === "horizontal" ? (
      <HorizontalBarChart chartData={chartData} compact={compact} />
    ) : (
      <VerticalBarChart chartData={chartData} compact={compact} />
    );
  } catch (error) {
    Sentry.captureException(error);
    return (
      <Alert
        type="error"
        title="Chart Error"
        message="Unable to render bar chart, invalid data."
        my={4}
      />
    );
  }
};
